import axios from 'axios';
import { AUTH_URL } from '../config/apiUrls';
import type { AuthTokensResponse } from '../types/auth';
import {
  clearTokens,
  getRefreshToken,
  setTokens,
} from '../utils/authTokens';
import { checkUserLibAdminAccess, isAdminOnlyEnabled } from '../utils/authPolicy';
import { clearSessionEncryptionKeyPair } from './encryptedApiResponse';

/** Bare client — no auth interceptors (avoids refresh/logout loops). */
const authSessionClient = axios.create({
  baseURL: AUTH_URL,
  headers: { 'Content-Type': 'application/json' },
});

let refreshPromise: Promise<string> | null = null;
let forcingLogout = false;

function redirectToLogin(): void {
  if (window.location.pathname === '/login') return;
  window.location.href = '/login';
}

/** Clear local session and send the user to login. */
export function forceLogout(): void {
  if (forcingLogout) return;
  forcingLogout = true;
  clearTokens();
  clearSessionEncryptionKeyPair();
  redirectToLogin();
  // Allow a later session after navigating back to login/app.
  queueMicrotask(() => {
    forcingLogout = false;
  });
}

/**
 * Rotate tokens via POST /auth/refresh.
 * Concurrent callers share one in-flight request.
 * On 401 (or missing refresh token): clear tokens and redirect to login.
 */
export async function refreshAccessToken(): Promise<string> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refresh_token = getRefreshToken();
    if (!refresh_token) {
      forceLogout();
      throw new Error('No refresh token');
    }

    try {
      const { data } = await authSessionClient.post<AuthTokensResponse>(
        '/auth/refresh',
        { refresh_token },
      );

      if (!data?.access_token || !data?.refresh_token) {
        forceLogout();
        throw new Error('Invalid refresh response');
      }

      if (isAdminOnlyEnabled() && !checkUserLibAdminAccess(data)) {
        forceLogout();
        throw new Error('Access denied: Lib Access policy required');
      }

      // Rotation: always replace the stored refresh token and policies.
      setTokens(
        {
          access_token: data.access_token,
          refresh_token: data.refresh_token,
        },
        data.activePolicies || data.policies,
      );
      return data.access_token;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        forceLogout();
      } else if (!getRefreshToken()) {
        forceLogout();
      } else {
        // Network / 5xx: do not clear session; let the caller fail.
      }
      throw error;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * POST /auth/logout with the stored refresh token.
 * Idempotent: always clears local tokens even if the request fails.
 */
export async function logoutSession(): Promise<void> {
  const refresh_token = getRefreshToken();
  try {
    if (refresh_token) {
      await authSessionClient.post('/auth/logout', { refresh_token });
    }
  } catch {
    // Best-effort; local clear still happens below.
  } finally {
    clearTokens();
  }
}
